import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { CONTAINER_STATUS, human } from "@/lib/statusLabels";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePickerField } from "@/components/DatePickerField";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Loader2, Ship } from "lucide-react";
import { PackingListImport } from "@/components/containers/PackingListImport";
import { TestResetButton } from "@/components/admin/TestResetButton"; // TEMPORARY (ID-030)
import { ExceptionWarning, UnassignedImportIssues } from "@/components/containers/ContainerExceptions";
import { useEffect } from "react";

const STATUS_BADGE = {
  IN_MOROCCO: "badge-neutral",
  DEPARTED: "badge-transit",
  ARRIVING: "badge-transit",
  RECEIVED: "badge-florida",
};

const fmtDateOnly = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10);
  return d.toLocaleDateString("en-US", { timeZone: "UTC", year: "numeric", month: "short", day: "numeric" });
};

export default function Containers() {
  const [containers, setContainers] = useState([]);
  const [issues, setIssues] = useState({});
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ container_number: "", carrier_ref: "", eta_warehouse: "" });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const q = search.trim().toUpperCase();
  const filteredContainers = containers.filter((c) => !q
    || (c.display_name || "").toUpperCase().includes(q)
    || (c.container_number || "").toUpperCase().includes(q)
    || (c.zc_ref || "").toUpperCase().includes(q)
    || (c.shipping_number || "").toUpperCase().includes(q)
    || (c.carrier_ref || "").toUpperCase().includes(q));

  const load = () => {
    setLoading(true);
    api.get("/container-import-exceptions/summary").then(({ data }) => setIssues(data)).catch(() => setIssues({}));
    api
      .get("/containers")
      .then((res) => setContainers(res.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    api.post("/container-tracking/sync-due").catch(() => {}).finally(load);
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/containers", {
        container_number: createForm.container_number,
        carrier_ref: createForm.carrier_ref,
        eta_warehouse: createForm.eta_warehouse || null,
      });
      toast.success(`Container ${data.container_number} created (draft — no inventory impact)`);
      setCreateOpen(false);
      setCreateForm({ container_number: "", carrier_ref: "", eta_warehouse: "" });
      navigate(`/admin/containers/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-containers-page">      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">Supply chain</p>
          <h1 className="mt-3 font-display text-4xl font-light tracking-tight text-charcoal md:text-5xl">Containers</h1>
        </div>
        <div className="flex w-full flex-wrap gap-3 sm:w-auto">
          <TestResetButton onDone={load} />
          <PackingListImport onCommitted={load} />
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-container-button" className="h-11 bg-terracotta text-white transition-all duration-200 hover:-translate-y-[1px] hover:bg-terracotta-hover">
                <Plus className="mr-2 h-4 w-4" /> New Container
              </Button>
            </DialogTrigger>
            <DialogContent className="border-border bg-white sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl font-medium text-charcoal">Create draft container</DialogTitle>
                <DialogDescription className="text-sm text-warmgrey">
                  Draft creation does not change Morocco inventory — allocation happens at packing-list confirmation. A business-facing name (ZELLIGIO-CONTAINER-NN-YYYY) is assigned automatically; the container number stays the internal identity.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreate} className="mt-2 space-y-5" data-testid="new-container-form">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Container number *</Label>
                  <Input required data-testid="container-number-input" value={createForm.container_number} onChange={(e) => setCreateForm((f) => ({ ...f, container_number: e.target.value }))} placeholder="CONT-1042" className="h-11 bg-white focus-visible:ring-terracotta" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Carrier reference</Label>
                  <Input data-testid="container-carrier-input" value={createForm.carrier_ref} onChange={(e) => setCreateForm((f) => ({ ...f, carrier_ref: e.target.value }))} className="h-11 bg-white focus-visible:ring-terracotta" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">ETA warehouse</Label>
                  <DatePickerField testId="container-eta-input" value={createForm.eta_warehouse} onChange={(v) => setCreateForm((f) => ({ ...f, eta_warehouse: v }))} />
                </div>
                <Button type="submit" disabled={saving} data-testid="create-container-submit" className="h-11 w-full bg-terracotta text-white hover:bg-terracotta-hover">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create container"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="mt-8">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search container name, number or carrier ref…" data-testid="containers-search" className="h-11 w-full max-w-md bg-white focus-visible:ring-terracotta" />
      </div>

      <div className="mt-4 overflow-x-auto rounded-md border border-border bg-white shadow-[0_2px_12px_rgba(26,26,24,0.04)]">
        <Table data-testid="containers-table" className="min-w-[900px]">
          <TableHeader>
            <TableRow className="border-border">
              <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Container</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Status</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Lines</TableHead>
              <TableHead className="text-right text-xs uppercase tracking-widest text-muted-foreground">Expected</TableHead>
              <TableHead className="text-right text-xs uppercase tracking-widest text-muted-foreground">Reserved</TableHead>
              <TableHead className="text-right text-xs uppercase tracking-widest text-muted-foreground">Sold</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">ETA warehouse</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="py-12 text-center text-sm text-muted-foreground">Loading…</TableCell></TableRow>
            ) : containers.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="py-12 text-center text-sm text-muted-foreground" data-testid="containers-empty">
                <Ship className="mx-auto mb-3 h-5 w-5 text-muted-foreground" />No containers yet. Import a packing list or create a draft.
              </TableCell></TableRow>
            ) : filteredContainers.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="py-12 text-center text-sm text-muted-foreground" data-testid="containers-no-match">No containers match your search.</TableCell></TableRow>
            ) : (
              filteredContainers.map((c) => (
                <TableRow
                  key={c.id}
                  data-testid={`container-row-${c.container_number}`}
                  onClick={() => navigate(`/admin/containers/${c.id}`)}
                  className="cursor-pointer border-border transition-colors duration-200 hover:bg-sand"
                >
                  <TableCell className="py-4">
                    <p className="text-sm font-semibold text-charcoal" data-testid={`container-name-${c.container_number}`}>{c.zc_ref || c.display_name || c.container_number}</p>
                    <p className="font-mono text-[11px] text-muted-foreground">Packing list #{c.container_number} · {c.shipping_number || "Shipping number pending"}</p>
                    {issues[c.id] && <div className="mt-2" onClick={(e) => e.stopPropagation()}><ExceptionWarning rows={issues[c.id].rows} boxes={issues[c.id].known_boxes} testid={`container-issues-${c.container_number}`}
                      onReview={() => navigate(`/admin/containers/${c.id}#import-exceptions`)} /></div>}
                  </TableCell>
                  <TableCell className="py-4"><span className={`badge-status ${STATUS_BADGE[c.status]}`}>{human(CONTAINER_STATUS, c.status)}</span>{c.quarantined && <span className="badge-status badge-neutral ml-2" data-testid={`container-quarantined-${c.id}`}>Quarantined — test data</span>}</TableCell>
                  <TableCell className="py-4 text-sm text-warmgrey">{c.line_count}</TableCell>
                  <TableCell className="py-4 text-right text-sm text-charcoal">{c.total_expected}</TableCell>
                  <TableCell className="py-4 text-right text-sm text-[#8a6435]">{c.total_reserved}</TableCell>
                  <TableCell className="py-4 text-right text-sm text-status-sold">{c.total_committed}</TableCell>
                  <TableCell className="py-4 text-sm text-warmgrey">{fmtDateOnly(c.eta_warehouse)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      <UnassignedImportIssues containers={containers} onChanged={load} />
    </div>
  );
}
